<template>
    <div class="activity-order-summary">
        <div class="activity-order-summary__header d-flex">
            <v-img
                max-width="150"
                class="me-3"
                tile
                :src="$filters.imageUrl(image, 600)"
                :lazy-src="$filters.srcHost('/placeholder.png')"
                @error="onImgError"
            >
                <template v-slot:placeholder>
                    <v-row v-show="!imgError && image" class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey-lighten-5" />
                    </v-row>
                </template>
            </v-img>
            <div>
                <div>
                    <span
                        :title="info.name"
                        class="activity-order-summary__header-name me-2 text-primary font-weight-medium text-subtitle-1"
                    >
                        {{ info.name }}
                    </span>
                    <v-chip v-if="product.type && product.type !== 'UNKNOWN'" label size="x-small">
                        {{ $t(`filters_activityType.${product.type}`) }}
                    </v-chip>
                </div>
                <supplier-name-label v-if="isTO1" :supplier-name="product.supplierName" class="mb-2" />
                <div v-if="info.shortDescription" class="mb-4 text-body-2" v-html="info.shortDescription" />
            </div>
        </div>
        <slot name="summary" :grouped-offers="groupedOffers" />
        <v-divider class="mb-2" />
        <div
            v-for="{offers, date} in groupedOffers"
            :key="date"
            class="activity-order-summary__content"
            :class="{'mb-5': groupedOffers.length > 1}"
        >
            <div v-if="groupedOffers.length > 1" class="text-body-2 text--secondary">
                {{ $filters.dateFormat(date) }}
            </div>
            <div v-for="offer in offers" :key="offer.offerKey" class="relative-position">
                <div
                    class="activity-order-summary__content-title d-flex align-center font-weight-bold cursor-pointer mb-1"
                    @click="offerInfo(offer)"
                >
                    <v-icon size="small" start>mdi-information-outline</v-icon>
                    <span>
                        {{ offer.serviceName }}
                    </span>
                    <span v-if="offerTime(offer)" class="ms-2 text-grey font-weight-bold text-body-2">
                        {{ `(${offerTime(offer)})` }}
                    </span>
                    <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
                </div>
                <div v-if="offer.available" class="activity-order-summary__available d-flex align-center text-caption">
                    <v-icon :title="$t('available')" start size="small" class="text-success">
                        mdi-checkbox-marked-circle
                    </v-icon>
                    <span class="text-success" v-text="$t('available')" />
                </div>
                <div v-else class="activity-order-summary__available text-caption d-flex align-center">
                    <v-icon :title="$t('on_request')" start size="small" class="text-warning">mdi-alert-circle</v-icon>
                    <span class="text-warning" v-text="$t('on_request')" />
                </div>
                <transfer-driver-languages
                    v-if="offer.languages"
                    :lang-codes="offer.languages.map(el => el.langCode)"
                />
                <template v-if="showPrice">
                    <conditions-label
                        :offer-key="offer.offerKey"
                        :price="offer.price"
                        class="text-caption"
                        @conditions="showConditionsInfo(offer)"
                    />
                    <div class="activity-order-summary__price mt-1 font-weight-bold">
                        <span>
                            {{ $filters.price(offerPrice(offer)) }}
                        </span>
                        <commission-amount :commission="offer.price.commission" />
                        <net-amount :price="offer.price" :search-request="searchRequest" />
                    </div>
                </template>
                <div v-else-if="free" class="mt-1 font-weight-bold">{{ $t('free') }}</div>
                <slot name="removeBtn" :offer-key="offer.offerKey" />
            </div>
        </div>
        <extra-services-summary
            v-if="extraServices && additionalOptions"
            :extra-services="extraServices"
            :additional-options="additionalOptions"
        />
        <add-ons-summary-list :selected-add-ons="selectedAddOns" />
        <div v-if="isShowSeats && seatsCount(productStore.seats[0])" class="mt-5">
            <p class="font-weight-bold">{{ $t('seat_selection') }}</p>
            <p class="text-caption">
                {{ $t('flights.seats') }}:
                {{ productStore.seats[0]?.map(({seatNumber}) => `${seatNumber}`).join(', ') }}
            </p>
            <strong v-if="seatsPrice(0)?.amount">
                {{ $filters.price(seatsPrice(0)) }} * {{ seatsCount(productStore.seats[0]) }} =
                {{ $filters.price(totalSeatPrice(productStore.seats[0])) }}
            </strong>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Prop} from 'vue-facing-decorator'
    import {clone} from '@/utils/helpers'
    import ConditionsLabel from '@/components/snippets/ConditionsLabel.vue'
    import {EventBus, SHOW_ACTIVITIES_INFO_MODAL, SHOW_ACTIVITY_CONDITIONS_INFO} from '@/utils/event-bus'
    import OrderSummaryLayout from '@/components/booking/OrderSummaryLayout.vue'
    import ExtraServicesSummary from '@/components/booking/snippets/ExtraServicesSummary.vue'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import NetAmount from '~src/components/snippets/NetAmount.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    // import AddOnsSummaryList from '@/components/booking/addOns/AddOnsSummaryList.vue'
    import {OrderSummaryBase} from '@/components/booking/OrderSummaryBase.vue'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'
    import TransferDriverLanguages from '~/components/snippets/TransferDriverLanguages.vue'

    @Component({
        components: {
            TransferDriverLanguages,
            ProductOfferTipsDialog,
            AddOnsSummaryList: defineAsyncComponent(() => import('@/components/booking/addOns/AddOnsSummaryList.vue')),
            ExtraServicesSummary,
            OrderSummaryLayout,
            ConditionsLabel,
            CommissionAmount,
            NetAmount,
            SupplierNameLabel,
        },
    })
    export class ActivityOrderSummary extends OrderSummaryBase {
        @Prop({required: true}) product
        @Prop({required: true, type: Array}) offers
        @Prop({required: true}) info
        @Prop({default: true}) prebooked
        @Prop({required: true, type: Array}) selectedAddOns
        @Prop({default: false}) isShowSeats

        imgError = false

        onImgError() {
            this.imgError = true
            return false
        }

        showConditionsInfo(offer) {
            EventBus.$emit(SHOW_ACTIVITY_CONDITIONS_INFO, offer)
        }

        offerInfo(offer) {
            EventBus.$emit(SHOW_ACTIVITIES_INFO_MODAL, {offer, info: this.info})
        }

        offerPrice(offer) {
            const price = this.prebooked && offer?.currentPrice ? clone(offer.currentPrice) : clone(offer.price)
            const mandatory =
                activitiesStore.bookingAdditionalOptions?.extraServices?.filter(
                    extraService => extraService.mandatory
                ) || []

            if (mandatory.length) {
                mandatory.forEach(item => {
                    price.amount -= item.salesTerms.find(el => el.type === 'CLIENT').price.amount
                    price.originalAmount -= item.salesTerms.find(el => el.type === 'CLIENT').originalAmount
                })
            }

            return price
        }

        offerTime(offer) {
            if (offer.startTimes?.length) {
                return offer.startTimes[0]
            }

            return null
        }

        get image() {
            if (!this.info.images) return null
            let main = this.info.images.find(image => image.mainImage)
            if (!main && this.info.images.length > 0) {
                main = this.info.images[0]
            }
            return main && main.url ? main.url : null
        }

        get isTO1() {
            return authStore.isTO1
        }

        get groupedOffers() {
            return this.offers
                .reduce((groups, offer) => {
                    const group = groups.find(group => group.date === offer.date)
                    if (group) {
                        group.offers.push(offer)
                    } else {
                        groups.push({
                            date: offer.date,
                            offers: [offer],
                        })
                    }
                    return groups
                }, [])
                .sort((gr1, gr2) => this.$dateFns.parseISO(gr1.date) - this.$dateFns.parseISO(gr2.date))
        }

        get showPrice() {
            return !this.free
        }

        get productStore() {
            return activitiesStore
        }

        seatsCount(seats) {
            return seats?.length || 0
        }

        seatsPrice() {
            return this.additionalOptions?.seatSelectionServices?.[0]?.price
        }

        totalSeatPrice(seats) {
            let price = clone(this.seatsPrice())
            price.amount = price.amount * this.seatsCount(seats)
            return price
        }
    }

    export default ActivityOrderSummary
</script>
